import { useCallback, useSyncExternalStore } from 'react';
import type { PendingFile } from './PendingFiles';

/**
 * Files staged in a task's message box, by task id, kept outside the box (SF-T-136): the task pop-up
 * rebuilding (the chat docking beside it or coming over it) or the Tasks panel closing doesn't drop them,
 * and an upload that finishes meanwhile still lands. This page only: File objects can't be stored.
 */
const NONE: PendingFile[] = [];
const staged = new Map<string, PendingFile[]>();
const listeners = new Set<() => void>();

const subscribe = (onChange: () => void) => {
  listeners.add(onChange);
  return () => void listeners.delete(onChange);
};

export function updateStagedFiles(taskId: string, change: (files: PendingFile[]) => PendingFile[]) {
  const next = change(staged.get(taskId) ?? NONE);
  if (next.length) staged.set(taskId, next);
  else staged.delete(taskId);
  listeners.forEach((listener) => listener());
}

export function useStagedFiles(taskId: string): [PendingFile[], (change: (files: PendingFile[]) => PendingFile[]) => void] {
  const files = useSyncExternalStore(subscribe, () => staged.get(taskId) ?? NONE, () => NONE);
  const update = useCallback((change: (files: PendingFile[]) => PendingFile[]) => updateStagedFiles(taskId, change), [taskId]);
  return [files, update];
}
